import type {
  FromModel,
  ModelDefinition,
  ModelValidateFn,
} from "@/__main__/data-model.mjs";
import createModel, {
  Any,
  arbitraryKeys,
  Mapped,
  Optional,
  Silent,
} from "@/__main__/data-model.mjs";

const UnitsModel = createModel({
  [arbitraryKeys]: {
    tier: Number,
    nb_games: Number,
    pick_rate: Number,
    avg_placement: Number,
    top_4_percent: Number,
    top_1_percent: Number,
    top_items: [String],
  },
});

const TraitsModel = createModel([
  {
    trait_api_name: String,
    nb_games: Number,
    nb_boards: Number,
    avg_placement: Number,
    top_4_percent: Number,
    top_1_percent: Number,
    pick_rate: Number,
    style: Number,
    trait_count: Optional(Number),
  },
]);

const ItemsModel = createModel({
  ...Mapped({
    key: String,
    value: {
      tier: Number,
      score: Optional(Number),
      nb_games: Number,
      pick_rate: Number,
      avg_placement: Number,
      top_4_percent: Number,
      top_1_percent: Number,
      item_type: String,
      craftable: Boolean,
    },
  }),
});

const AugmentsModel = createModel({
  ...Mapped({
    key: String,
    value: {
      nb_games: Number,
      avg_placement: Optional(Number),
      top_4_percent: Optional(Number),
      top_1_percent: Optional(Number),
      pick_rate: Optional(Number),
      tier: Number,
      top_comps: [Number],
      top_users: Silent([Silent(String)]),
      "2_1": Optional(Number),
      "3_2": Optional(Number),
      "4_2": Optional(Number),
    },
  }),
});

const LegendsModel = createModel({
  [arbitraryKeys]: {
    tier: Number,
    pick_rate: Number,
    augments_stats: Silent({
      [arbitraryKeys]: {
        board_pick_rate: Number,
        avg_placement: Number,
        tier: Number,
      },
    }),
  },
});

const UnitModel = createModel({
  tier: Number,
  pick_rate: Number,
  avg_placement: Number,
  top_4_percent: Number,
  top_1_percent: Number,
  item_trios: [
    {
      pick_rate_rank: Number,
      item_api_names: [String],
      avg_placement: Number,
      nb_boards: Number,
      pick_rate: Number,
    },
  ],
  top_augments: [
    {
      augment_rank: Number,
      augment_api_name: String,
      augment_avg_augment_unit_pick_rate: Number,
      augment_unit_pick_rate: Number,
      pick_rate_improvement: Number,
      augment_unit_avg_placement: Number,
      nb_boards: Number,
    },
  ],
  nb_items_stats: {
    [arbitraryKeys]: {
      nb_boards: Number,
      avg_placement: Number,
      distribution: Number,
    },
  },
  level_stats: {
    [arbitraryKeys]: {
      nb_boards: Number,
      avg_placement: Number,
      distribution: Number,
    },
  },
  past_days_stats: [
    {
      dt: String,
      nb_boards: Number,
      nb_games: Number,
      avg_placement: Number,
      top_1_percent: Number,
      top_4_percent: Number,
      pick_rate: Number,
    },
  ],
  past_patches_stats: [
    {
      patch: String,
      nb_boards: Number,
      nb_games: Number,
      avg_placement: Number,
      top_1_percent: Number,
      top_4_percent: Number,
      pick_rate: Number,
    },
  ],
});

const compUnitsModel = [
  {
    key: Optional(String),
    name: String,
    pick_rate: Number,
    avg_placement: Number,
    round_winrate: Optional(Number),
    streak_rate: Optional(Number),
    level_stats: {
      [arbitraryKeys]: {
        nb_boards: Number,
        avg_placement: Number,
        distribution: Number,
      },
    },
    nb_items_stats: {
      [arbitraryKeys]: {
        nb_boards: Number,
        avg_placement: Number,
        distribution: Number,
      },
    },
    item_trios: [
      {
        pick_rate_rank: Number,
        item_api_names: [String],
        avg_placement: Number,
        top_1_percent: Number,
        nb_boards: Number,
        pick_rate: Number,
      },
    ],
    top_items: Optional([
      {
        pick_rate_rank: Number,
        item_api_name: String,
        avg_placement: Number,
        top_1_percent: Number,
        nb_boards: Number,
        pick_rate: Number,
      },
    ]),
  },
] satisfies ModelDefinition;

const CompUnitsModel = createModel(compUnitsModel);

const compItemsModel = [
  {
    name: String,
    pick_rate: Number,
    avg_placement: Number,
    nb_boards: Number,
    nb_games: Number,
    item_type: Optional(String),
    round_winrate: Optional(Number),
    streak_rate: Optional(Number),
    holders: [
      {
        name: String,
        pick_rate: Number,
        avg_placement: Number,
        top_1_percent: Number,
        top_4_percent: Number,
      },
    ],
  },
] satisfies ModelDefinition;

const CompItemsModel = createModel(compItemsModel);

const compAugmentsModel = [
  {
    key: Optional(String),
    name: String,
    pick_rate: Number,
    pick_rate_improvement: Optional(Number),
    pick_rate_relative_improvement: Optional(Number),
    avg_placement: Number,
    round_winrate: Optional(Number),
    streak_rate: Optional(Number),
    round_winrate_improvement: Optional(Number),
    pick_order: {
      [arbitraryKeys]: Number,
    },
  },
] satisfies ModelDefinition;

const CompAugmentsModel = createModel(compAugmentsModel);

const CompLegendsModel = createModel([
  {
    legend_id: String,
    pick_rate: Number,
    avg_placement: Number,
    nb_boards: Number,
    nb_games: Number,
    augments: [
      {
        name: String,
        pick_rate: Number,
        pick_rate_improvement: Optional(Number),
        pick_rate_relative_improvement: Optional(Number),
        nb_boards: Number,
      },
    ],
  },
]);

const CarryItemHoldersLateModel = createModel(
  Silent([
    {
      end_game_carry_api_name: String,
      stats: [
        {
          early_game_carry_api_name: String,
          stats: {
            avg_similarity: Number,
            carry_holder_rank: Number,
            combined_nb_boards: Number,
          },
        },
      ],
    },
  ]),
);

const CompsUnits = [
  {
    name: String,
    star_level: Optional(Number),
    items: Silent([String]),
    core: Optional(Boolean),
    avg_nb_items: Optional(Number),
    end_game_carries: Optional([String]),
    playCondition: Optional(Boolean),
  },
] satisfies ModelDefinition;

const DistributionsModal = {
  pick_rate_distribution: Mapped({ key: Number, value: Number }),
  placement_distribution: Mapped({ key: Number, value: Number }),
  rank_pick_rate_distribution: Mapped({ key: String, value: Number }),
  rank_placement_distribution: Mapped({ key: String, value: Number }),
};

const LevelVariation = {
  avg_placement: Number,
  comps_rank: Number,
  nb_boards: Number,
  units: [
    {
      name: String,
      items: [String],
      end_game_carries: Optional([String]),
      end_game_carry_api_name: Optional(String),
      star_level: Optional(Number),
      core: Optional(Boolean),
    },
  ],
} satisfies ModelDefinition;

const CompsModel = createModel(
  Silent([
    {
      cluster_id: Number,
      api_name: Optional(String),
      name: Optional(String),
      units: CompsUnits,
      stats: {
        tier: Number,
        pick_rate: Number,
        avg_placement: Number,
        top_4_percent: Number,
        top_1_percent: Number,
        nb_boards: Number,
        nb_games: Number,
      },
      tags: [String],
      board_strength: Optional({
        board_strength_mean_std: {
          count: Number,
          mean: Number,
          stdev: Number,
        },
        board_strength_p95: Number,
        placement_mean_board_strength: Mapped({ key: Number, value: Number }),
        placement_num_boards: Mapped({ key: Number, value: Number }),
        placement_sum_board_strengths: Mapped({ key: Number, value: Number }),
      }),
      carry_units: [String],
      carousel_priority: [String],
      augments: [String],
      legends: [String],

      variations: {
        levels: {
          4: [LevelVariation],
          5: [LevelVariation],
          6: [LevelVariation],
          7: [LevelVariation],
          8: [LevelVariation],
          9: [LevelVariation],
          10: [LevelVariation],
        },
      },

      units_stats: compUnitsModel,
      items_stats: compItemsModel,
      augments_stats: compAugmentsModel,

      strategy: Optional({
        type: Optional(String),
        level_stage: Mapped({ key: String, value: String }),
        round_leveling_gold: Mapped({ key: String, value: Optional(Number) }),
        round_rolling_gold: Mapped({ key: String, value: Number }),
        level_rolling_gold: Mapped({ key: String, value: Number }),
      }),
      distributions: DistributionsModal,
      win_conditions: [
        {
          score_rank: Silent(Number),
          type: String,
          name: String,
          pick_rate: Number,
          avg_placement_improvement: Optional(Number),
          avg_placement_without: Optional(Number),
          avg_placement: Silent(Number),
          score: Silent(Number),
          unit_star_level: Optional(Number),
          trait_min_count: Optional(Number),
          condition_rank: Optional(Number),
          subtype: Silent(String),
          tier: Optional(Number),
          checked: Optional(Boolean),
        },
      ],
      play_conditions: [
        {
          score_rank: Silent(Number),
          type: String,
          name: String,
          pick_rate: Number,
          avg_placement_improvement: Optional(Number),
          avg_placement_without: Optional(Number),
          avg_placement: Silent(Number),
          score: Silent(Number),
          unit_star_level: Optional(Number),
          trait_min_count: Optional(Number),
          condition_rank: Optional(Number),
          subtype: Silent(String),
          tier: Optional(Number),
          checked: Optional(Boolean),
        },
      ],
      carry_item_holders: CarryItemHoldersLateModel,
      top_early_comps: Silent([
        {
          analyzed_comps_avg_placement_improvement: Number,
          analyzed_comps_pick_rate: Number,
          carry_item_holders: CarryItemHoldersLateModel,
          combined_avg_placement: Number,
          combined_nb_boards: Number,
          early_comps_cluster_id: String,
          rank: Number,
          score: Silent(Number),
        },
      ]),
      top_analyzed_comps: Optional([
        {
          analyzed_comps_avg_placement_improvement: Number,
          analyzed_comps_pick_rate: Number,
          carry_item_holders: CarryItemHoldersLateModel,
          combined_avg_placement: Number,
          combined_nb_boards: Number,
          rank: Number,
          score: Silent(Number),
        },
      ]),
      special_units: Optional(CompsUnits),
      units_positions: Optional([
        {
          unit_api_name: String,
          board_position: Number,
        },
      ]),
      headliner_stats: Optional([
        {
          api_name_generic: String,
          avg_placement: Number,
          headlinerBonusTraitArray: [String],
          headliner_rank: Number,
          nb_boards: Optional(Number),
          pick_rate: Number,
        },
      ]),
      powerup_units: Optional({
        early_powerup_units: [
          {
            powerup_rank: Number,
            unit_api_name: String,
          },
        ],
        late_powerup_units: [
          {
            powerup_rank: Number,
            unit_api_name: String,
          },
        ],
      }),
    },
  ]),
);

const CarryItemHoldersEarlyModel = createModel(
  Silent([
    {
      early_game_carry_api_name: String,
      stats: [
        {
          end_game_carry_api_name: String,
          stats: {
            carry_holder_rank: Number,
            avg_similarity: Number,
            combined_nb_boards: Number,
            interchangeable: Optional(Boolean),
          },
        },
      ],
    },
  ]),
);

const EarlyCompsModel = createModel(
  Silent([
    {
      cluster_id: Number,
      name: String,
      api_name: String,
      units: CompsUnits,
      stats: {
        tier: Number,
        pick_rate: Number,
        nb_boards: Number,
        nb_games: Number,
        avg_placement: Number,
        top_4_percent: Number,
        top_1_percent: Number,
        round_winrate: Optional(Number),
        streak_rate: Number,
      },
      tags: [String],
      carry_units: [String],
      carousel_priority: [String],
      augments: [String],
      variations: Mapped({
        key: String,
        value: [
          {
            units: CompsUnits,
            avg_placement: Number,
            round_winrate: Number,
            nb_boards: Number,
            top_4_percent: Silent(Number),
            top_1_percent: Silent(Number),
          },
        ],
      }),
      strategy: Optional({
        streak_strategy: Optional(String),
      }),
      carry_item_holders: CarryItemHoldersEarlyModel,
      top_analyzed_comps: Silent([
        {
          rank: Number,
          analyzed_comps_cluster_id: String,
          combined_nb_boards: Number,
          combined_avg_placement: Number,
          early_comps_avg_placement_improvement: Number,
          early_comps_pick_rate: Optional(Number),
          score: Number,
          carry_item_holders: CarryItemHoldersEarlyModel,
        },
      ]),
      itemization_tier_list: Silent([
        {
          score_rank: Number,
          tier: Number,
          item_api_name: String,
          streak_item: Boolean,
          top_users: [String],
          score: Silent(Number),
          pick_rate: Number,
          avg_placement: Number,
          round_winrate: Number,
          streak_rate: Number,
        },
      ]),
      distributions: DistributionsModal,
      legends: Optional([String]),
      special_units: Optional(CompsUnits),
      units_positions: Optional([
        {
          unit_api_name: String,
          board_position: Number,
        },
      ]),
      top_early_comps: Optional([Any]),
      play_conditions: Optional([Any]),
    },
  ]),
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TftStatsModel = (page, tab): ModelValidateFn<any> => {
  if (page === "legends") return LegendsModel;
  if (page === "augments") return AugmentsModel;
  if (page === "traits") return TraitsModel;
  if (page === "items") return ItemsModel;
  if (page === "units") return UnitsModel;
  if (page === "indiv_units") return UnitModel;
  if (page === "compDetails") {
    if (tab === "units") return CompUnitsModel;
    if (tab === "items") return CompItemsModel;
    if (tab === "augments") return CompAugmentsModel;
    if (tab === "legends") return CompLegendsModel;
    if (tab === "powerups") return CompUnitsModel;
  }
  if (page === "comps") return CompsModel;
  if (page === "earlycompDetails") {
    if (tab === "units") return CompUnitsModel;
    if (tab === "items") return CompItemsModel;
    if (tab === "augments") return CompAugmentsModel;
    if (tab === "legends") return CompLegendsModel;
    if (tab === "powerups") return CompUnitsModel;
  }
  if (page === "earlycomps") return EarlyCompsModel;
};

export type TftStats = FromModel<typeof TftStatsModel>;

export default TftStatsModel;
